/**
 * La risposta dell'assistente, dal Markdown del modello: paragrafi,
 * grassetti, elenchi, titoletti e tabelle. L'HTML scritto dal modello non si
 * rende; i link ai percorsi dell'applicazione diventano pulsanti, gli altri
 * restano testo.
 */

import type { ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowRightIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { conCollegamenti, percorsoApp } from './testo.js';

const titoletto = ({ children }: { children?: ReactNode }) => (
  <p className="mt-3 mb-1 font-semibold first:mt-0">{children}</p>
);

export function TestoRisposta({
  testo,
  onVai,
}: {
  testo: string;
  onVai: (percorso: string) => void;
}) {
  const componenti: Components = {
    p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
    h1: titoletto,
    h2: titoletto,
    h3: titoletto,
    h4: titoletto,
    h5: titoletto,
    h6: titoletto,
    ul: ({ children }) => (
      <ul className="my-2 flex list-disc flex-col gap-1 pl-5 first:mt-0 last:mb-0">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="my-2 flex list-decimal flex-col gap-1 pl-5 first:mt-0 last:mb-0">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="pl-0.5 marker:text-muted-foreground">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    code: ({ children }) => (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
    ),
    pre: ({ children }) => <>{children}</>,
    hr: () => <hr className="my-3 border-border" />,
    blockquote: ({ children }) => (
      <blockquote className="my-2 border-l-2 border-border pl-3 text-muted-foreground">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="my-2 first:mt-0 last:mb-0">
        <Table>{children}</Table>
      </div>
    ),
    thead: ({ children }) => <TableHeader>{children}</TableHeader>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => <TableHead>{children}</TableHead>,
    td: ({ children }) => <TableCell>{children}</TableCell>,
    img: () => null,
    a: ({ href, children }) => {
      const percorso = percorsoApp(href);
      if (!percorso) return <>{children}</>;
      return (
        <Button
          type="button"
          variant="default"
          size="sm"
          className="my-1 inline-flex h-auto py-1.5 whitespace-normal"
          onClick={() => onVai(percorso)}
        >
          {children}
          <ArrowRightIcon data-icon="inline-end" />
        </Button>
      );
    },
  };

  return (
    <Markdown remarkPlugins={[remarkGfm]} components={componenti} skipHtml>
      {conCollegamenti(testo)}
    </Markdown>
  );
}
